import { AccessModes, Dimensions, Metrics, SortOrder, Sources, WITH_ID_SUFFIX } from 'components/insights/constants';
import { HEALTH_PLAN_CLASSIFICATION, TRANSFERRED_FROM_CLIENT } from 'constants/filterKeysConstants';

import { BusinessDevelopmentQueryParams } from './query/useBusinessDevelopmentAnalytics';

// --- View By ---

export const VIEW_BY = { REFERRAL_SOURCE: 'referralSource', PAYER_SOURCE: 'payerSource' } as const;
export type ViewBy = (typeof VIEW_BY)[keyof typeof VIEW_BY];

// --- Table Pivot ---

export const TABLE_PIVOT = {
  PATIENT_VOLUME: 'patientVolume',
  DISCHARGES: 'discharges',
  REFERRALS: 'referrals',
} as const;
export type TablePivot = (typeof TABLE_PIVOT)[keyof typeof TABLE_PIVOT];

export const VIEW_BY_OPTIONS = [
  { label: 'Referral Source', value: VIEW_BY.REFERRAL_SOURCE },
  { label: 'Payer Source', value: VIEW_BY.PAYER_SOURCE },
] as const;

export const viewByDimension = (viewBy: string): Dimensions =>
  viewBy === VIEW_BY.PAYER_SOURCE ? Dimensions.HEALTH_PLAN_CLASSIFICATION : Dimensions.TRANSFERRED_FROM_CLIENT;

export const viewBySlug = (viewBy: ViewBy): string =>
  viewBy === VIEW_BY.PAYER_SOURCE ? 'payer-source' : 'referral-source';

export const pivotSlug = (tablePivot: TablePivot): string =>
  tablePivot === TABLE_PIVOT.PATIENT_VOLUME ? 'patient-volume' : tablePivot;

// --- Table Pivot ---

export const TABLE_PIVOT_OPTIONS = [
  { label: 'Patient Volume', value: TABLE_PIVOT.PATIENT_VOLUME },
  { label: 'Discharges', value: TABLE_PIVOT.DISCHARGES },
  { label: 'Referrals', value: TABLE_PIVOT.REFERRALS },
] as const;

// --- Base Query Params ---
// Each pivot's base params define source, metrics, and default sort. Table and export
// builders derive from these — table adds :with_id + rollups, export uses plain dimensions.

const CARE_TRANSITIONS_BASE: BusinessDevelopmentQueryParams = {
  source: Sources.INSIGHTS_CARE_TRANSITIONS,
  accessModes: [AccessModes.MANAGER],
  metrics: [Metrics.LOCATION_EPISODE_ID_COUNT, Metrics.LENGTH_OF_STAY_AVERAGE],
  sortBy: `${Metrics.LOCATION_EPISODE_ID_COUNT} ${SortOrder.DESC}`,
};

const DISCHARGES_BASE: BusinessDevelopmentQueryParams = {
  source: Sources.LOCATION_EPISODE_DISCHARGES,
  accessModes: [AccessModes.MANAGER],
  metrics: [Metrics.ID_COUNT, Metrics.READMISSION_RATE],
  sortBy: `${Metrics.ID_COUNT} ${SortOrder.DESC}`,
};

const REFERRALS_BASE: BusinessDevelopmentQueryParams = {
  source: Sources.INSIGHTS_REFERRALS,
  accessModes: [AccessModes.MANAGER],
  metrics: [
    Metrics.REVIEW_ID_COUNT,
    Metrics.ACCEPTANCE_RATE,
    Metrics.DENIAL_RATE,
    Metrics.CREATED_TO_DISCHARGE_DAYS_AVERAGE,
    Metrics.ACCEPTED_TO_DISCHARGE_DAYS_AVERAGE,
  ],
  sortBy: `${Metrics.REVIEW_ID_COUNT} ${SortOrder.DESC}`,
};

// --- Table Params (with :with_id + rollups) ---

export const buildCareTransitionsParams = (dimension: Dimensions): BusinessDevelopmentQueryParams => ({
  ...CARE_TRANSITIONS_BASE,
  dimensions: [`${dimension}${WITH_ID_SUFFIX}`],
  rollups: true,
});

export const buildDischargesParams = (dimension: Dimensions): BusinessDevelopmentQueryParams => ({
  ...DISCHARGES_BASE,
  dimensions: [`${dimension}${WITH_ID_SUFFIX}`],
  rollups: true,
});

export const buildReferralsTableParams = (dimension: Dimensions): BusinessDevelopmentQueryParams => ({
  ...REFERRALS_BASE,
  dimensions: [`${dimension}${WITH_ID_SUFFIX}`],
  rollups: true,
});

// --- Export Params (plain dimensions, no rollups) ---

export const buildPatientVolumeExportParams = (dimension: Dimensions): BusinessDevelopmentQueryParams => ({
  ...CARE_TRANSITIONS_BASE,
  dimensions: [dimension],
});

export const buildDischargesExportParams = (dimension: Dimensions): BusinessDevelopmentQueryParams => ({
  ...DISCHARGES_BASE,
  dimensions: [dimension],
});

export const buildReferralsExportParams = (dimension: Dimensions): BusinessDevelopmentQueryParams => ({
  ...REFERRALS_BASE,
  dimensions: [dimension],
});

// --- Slideout ---

// Build the entity filter that locks a slideout to a single entity based on which viewBy was
// active when the entity was clicked.
export const entityFilterForViewBy = (
  viewBy: ViewBy,
  entityId: string,
  entityName: string
): Record<string, unknown> => {
  const key = viewBy === VIEW_BY.PAYER_SOURCE ? HEALTH_PLAN_CLASSIFICATION : TRANSFERRED_FROM_CLIENT;
  return { [key]: [{ id: entityId, name: entityName }] };
};

// Slideout View By options — vary by the landing page viewBy.
// For referralSource: Referral Group, SNF, {GroupType} Company (with expandable sub-rows)
// For payerSource: Plan Type, SNF, {GroupType} Company (with expandable sub-rows)
export type SlideoutViewByOption = {
  label: string;
  value: string;
  // Dimensions per source — referrals uses different attribute names for the same concepts.
  careTransitionDimensions: string[];
  dischargeDimensions: string[];
  referralDimensions: string[];
  hasSubRows: boolean;
};

export const buildSlideoutViewByOptions = (viewBy: ViewBy, groupTypeName: string): SlideoutViewByOption[] => {
  const companyOption: SlideoutViewByOption = {
    label: `${groupTypeName} Company`,
    value: Dimensions.PROVIDER_CLIENT,
    careTransitionDimensions: [Dimensions.PROVIDER_CLIENT, Dimensions.GROUP],
    dischargeDimensions: [Dimensions.PROVIDER_CLIENT, Dimensions.GROUP_NAME],
    referralDimensions: [Dimensions.RECEIVING_CLIENT, Dimensions.RECEIVING_GROUP],
    hasSubRows: true,
  };

  const snfOption: SlideoutViewByOption = {
    label: 'SNF',
    value: Dimensions.PREDECESSOR_GROUP,
    careTransitionDimensions: [Dimensions.PREDECESSOR_GROUP],
    dischargeDimensions: [Dimensions.PREDECESSOR_GROUP],
    referralDimensions: [Dimensions.SENDING_GROUP],
    hasSubRows: false,
  };

  if (viewBy === VIEW_BY.PAYER_SOURCE) {
    return [
      {
        label: 'Plan Type',
        value: Dimensions.PLAN_TYPE_CLASSIFICATION,
        careTransitionDimensions: [Dimensions.PLAN_TYPE_CLASSIFICATION],
        dischargeDimensions: [Dimensions.PLAN_TYPE_CLASSIFICATION],
        referralDimensions: [Dimensions.PLAN_TYPE_CLASSIFICATION],
        hasSubRows: false,
      },
      snfOption,
      companyOption,
    ];
  }

  return [
    {
      label: 'Hospital',
      value: Dimensions.TRANSFERRED_FROM,
      careTransitionDimensions: [Dimensions.TRANSFERRED_FROM],
      dischargeDimensions: [Dimensions.TRANSFERRED_FROM],
      referralDimensions: [Dimensions.TRANSFERRED_FROM],
      hasSubRows: false,
    },
    snfOption,
    companyOption,
  ];
};

export const getDefaultSlideoutDimension = (viewBy: ViewBy): string =>
  viewBy === VIEW_BY.PAYER_SOURCE ? Dimensions.PLAN_TYPE_CLASSIFICATION : Dimensions.TRANSFERRED_FROM;

// Slideout query param builders — each picks the correct dimensions for its source.
export const buildSlideoutCareTransitionsParams = (option: SlideoutViewByOption): BusinessDevelopmentQueryParams => ({
  source: Sources.INSIGHTS_CARE_TRANSITIONS,
  accessModes: [AccessModes.MANAGER],
  dimensions: option.careTransitionDimensions,
  metrics: [Metrics.LOCATION_EPISODE_ID_COUNT, Metrics.LENGTH_OF_STAY_AVERAGE],
  sortBy: `${Metrics.LOCATION_EPISODE_ID_COUNT} ${SortOrder.DESC}`,
  rollups: true,
});

export const buildSlideoutDischargesParams = (option: SlideoutViewByOption): BusinessDevelopmentQueryParams => ({
  source: Sources.LOCATION_EPISODE_DISCHARGES,
  accessModes: [AccessModes.MANAGER],
  dimensions: option.dischargeDimensions,
  metrics: [Metrics.ID_COUNT, Metrics.READMISSION_RATE],
  sortBy: `${Metrics.ID_COUNT} ${SortOrder.DESC}`,
  rollups: true,
});

export const buildSlideoutReferralsParams = (option: SlideoutViewByOption): BusinessDevelopmentQueryParams => ({
  source: Sources.INSIGHTS_REFERRALS,
  accessModes: [AccessModes.MANAGER],
  dimensions: option.referralDimensions,
  metrics: [
    Metrics.REVIEW_ID_COUNT,
    Metrics.ACCEPTANCE_RATE,
    Metrics.DENIAL_RATE,
    Metrics.CREATED_TO_DISCHARGE_DAYS_AVERAGE,
    Metrics.ACCEPTED_TO_DISCHARGE_DAYS_AVERAGE,
  ],
  sortBy: `${Metrics.REVIEW_ID_COUNT} ${SortOrder.DESC}`,
  rollups: true,
});

const SLIDEOUT_PIVOT_CONFIG: Record<
  TablePivot,
  {
    builder: (o: SlideoutViewByOption) => BusinessDevelopmentQueryParams;
    dimensions: (o: SlideoutViewByOption) => string[];
  }
> = {
  [TABLE_PIVOT.PATIENT_VOLUME]: {
    builder: buildSlideoutCareTransitionsParams,
    dimensions: (o) => o.careTransitionDimensions,
  },
  [TABLE_PIVOT.DISCHARGES]: { builder: buildSlideoutDischargesParams, dimensions: (o) => o.dischargeDimensions },
  [TABLE_PIVOT.REFERRALS]: { builder: buildSlideoutReferralsParams, dimensions: (o) => o.referralDimensions },
};

export const buildSlideoutExportParams = (
  tablePivot: TablePivot,
  option: SlideoutViewByOption
): BusinessDevelopmentQueryParams => {
  const { builder, dimensions } = SLIDEOUT_PIVOT_CONFIG[tablePivot];
  return {
    ...builder(option),
    dimensions: dimensions(option).map((d) => d.replace(WITH_ID_SUFFIX, '')),
    rollups: undefined,
  };
};
